import {
  AnchorButton,
  Classes,
  Collapse,
  Divider,
  H5,
  Spinner,
} from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import classNames from 'classnames';
import * as React from 'react';

import { projectsActionCreators } from 'src/actions/projects/actionCreators';
import {
  DataSourceTable,
  DataSourceTableSelectItem,
} from 'src/constants/dataSourceTable';
import { QueryStatus } from 'src/constants/virtualGraphs';
import { DataSourcePanelContainer } from 'src/containers/ProjectResourceDialog/DataSourcePanelContainer';
import {
  ProjectVirtualGraphToCreate,
  ProjectVirtualGraphsToCreate,
} from 'src/containers/ProjectResourceDialog/ProjectVirtualGraphCreationPanel/types';
import { ProjectVirtualGraphPanelProperties } from 'src/containers/ProjectVirtualGraphPanel/ProjectVirtualGraphPanelProperties';
import { ProjectVirtualGraphPanelScope } from 'src/containers/ProjectVirtualGraphPanel/ProjectVirtualGraphPanelScope';
import { ProjectVirtualGraphPreview } from 'src/containers/ProjectVirtualGraphPanel/ProjectVirtualGraphPreview';
import { getCustomQueryFromVgsToCreate } from 'src/containers/ProjectVirtualGraphPanel/utils/getCustomQueryFromVgsToCreate';
import { getTablesFromVgsToCreate } from 'src/containers/ProjectVirtualGraphPanel/utils/getTablesFromVgsToCreate';
import { sortVirtualGraphs } from 'src/containers/ProjectVirtualGraphPanel/utils/sortVirtualGraphs';
import { connectionSelector } from 'src/selectors/connection';
import { isConnectedSelector } from 'src/selectors/isConnected';
import { isStardogFeatureSupportedSelector } from 'src/selectors/isStardogFeatureSupported';
import { DataSource } from 'src/store/state/dataSources';
import {
  ProjectVirtualGraph,
  ProjectVirtualGraphField,
  ProjectVirtualGraphOptions,
} from 'src/store/state/projects';
import * as copy from 'src/templates/copy';
import { generateDateRandomId } from 'src/utils/generateId';
import { generateSqlQueryForTable } from 'src/utils/generateSqlQueryForTable';
import { useDidValueChange } from 'src/utils/hooks/useDidValueChange';
import { useProjectDataSourceQuery } from 'src/utils/hooks/useProjectDataSourceQuery';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';

type QueueItem = {
  from: DataSourceTableSelectItem | string;
  virtualGraphId: string;
};

export type ProjectVirtualGraphPanelRefAttributes = {
  abortQuery: () => void;
} | null;

export type ProjectVirtualGraphPanelContents = {
  connectionId?: string;
  fields: ProjectVirtualGraphField[];
  previewRows: string[][];
};

export type ProjectVirtualGraphPanelProps = {
  dataSource: DataSource | null;
  disableDataSourceSidebar?: boolean;
  disableEditing?: boolean;
  enablePreviewRefresh?: boolean;
  existingVirtualGraph?: ProjectVirtualGraph;
  handleAddVirtualGraph?: (virtualGraph: ProjectVirtualGraphToCreate) => void;
  handleRemoveVirtualGraph?: (id: string) => void;
  handleUpdateVirtualGraph?: (
    id: string,
    update: Partial<ProjectVirtualGraphToCreate>
  ) => void;
  isPendingTables?: boolean;
  isUsingCustomQuery: boolean;
  nativeQuoteCharacter: string | null;
  selectedTab: string;
  setErrorText: (errorText: string) => void;
  setIsUsingCustomQuery: (isUsingCustomQuery: boolean) => void;
  setSelectedTab: (selectedTab: string) => void;
  setVirtualGraphOptions: (fileOptions: ProjectVirtualGraphOptions) => void;
  tables: DataSourceTable[];
  updateVirtualGraphOptions: (
    update: Partial<ProjectVirtualGraphOptions>
  ) => void;
  virtualGraphOptions: ProjectVirtualGraphOptions;
  virtualGraphsToCreate: ProjectVirtualGraphsToCreate;
};

export const ProjectVirtualGraphPanel: React.VFC<
  ProjectVirtualGraphPanelProps
> = ({
  dataSource,
  disableDataSourceSidebar,
  disableEditing,
  enablePreviewRefresh,
  existingVirtualGraph,
  handleAddVirtualGraph,
  handleRemoveVirtualGraph,
  handleUpdateVirtualGraph,
  isPendingTables,
  isUsingCustomQuery,
  nativeQuoteCharacter,
  selectedTab,
  setErrorText,
  setIsUsingCustomQuery,
  setSelectedTab,
  setVirtualGraphOptions,
  tables,
  updateVirtualGraphOptions,
  virtualGraphOptions,
  virtualGraphsToCreate,
}) => {
  const dispatch = useReduxDispatch();

  const currentConnection = useReduxSelector(connectionSelector);
  const isConnected = useReduxSelector(isConnectedSelector);
  const isCurrentConnectionIncorrect =
    !currentConnection ||
    (existingVirtualGraph &&
      (!existingVirtualGraph.connectionId ||
        existingVirtualGraph.connectionId !== currentConnection.id));
  const isDataSourceQueryOptionsSupported = useReduxSelector(
    isStardogFeatureSupportedSelector(
      'isDataSourceQueryOptionsSupported',
      currentConnection
    )
  );

  const [forceRefresh, setForceRefresh] = React.useState<boolean>(false);
  const [customQueryText, setCustomQueryText] = React.useState<string>(
    (getCustomQueryFromVgsToCreate(virtualGraphsToCreate)?.from as string) || ''
  );
  const [runningQueryQueue, setRunningQueryQueue] = React.useState<
    Array<{
      virtualGraphId: string;
      from: DataSourceTableSelectItem | string;
    }>
  >([]);

  const addItemToQueue = React.useCallback(
    (...queueItems: QueueItem[]) => {
      queueItems.forEach(({ from, virtualGraphId }) => {
        if (virtualGraphsToCreate[virtualGraphId]) {
          handleUpdateVirtualGraph?.(virtualGraphId, {
            from,
            fields: [],
            previewRows: [],
          });
        } else {
          handleAddVirtualGraph?.({
            id: virtualGraphId,
            name:
              typeof from === 'string'
                ? `${dataSource?.id || ''} Custom Query`.trimStart()
                : from.name,
            from,
            fields: [],
            previewRows: [],
            allResultsIncluded: false,
          });
        }
      });
      setRunningQueryQueue((prevQueue: any[]) => {
        const newRunningQueryQueue = [...prevQueue];
        newRunningQueryQueue.push(...queueItems);
        return newRunningQueryQueue;
      });
    },
    [
      dataSource,
      handleAddVirtualGraph,
      handleUpdateVirtualGraph,
      virtualGraphsToCreate,
    ]
  );

  let currentQueryQueueItem: QueueItem | undefined;
  if (runningQueryQueue?.length) {
    [currentQueryQueueItem] = runningQueryQueue;
  }

  let vgQuery = '';
  if (currentQueryQueueItem) {
    vgQuery =
      typeof currentQueryQueueItem.from === 'string'
        ? currentQueryQueueItem.from
        : generateSqlQueryForTable({
            table: currentQueryQueueItem.from,
            nativeQuoteCharacter,
            options: virtualGraphOptions,
            limit: true,
          });
  }

  const runningQueryDataSourceId =
    existingVirtualGraph?.dataSourceId || dataSource?.id;
  const runningQueryFields = existingVirtualGraph?.fields;
  const runningQueryOptions = isDataSourceQueryOptionsSupported
    ? virtualGraphOptions
    : undefined;
  const runningQueryArgs = React.useMemo(
    () => ({
      dataSourceId: runningQueryDataSourceId,
      existingFields: runningQueryFields,
      forceRefresh,
      isCurrentConnectionIncorrect,
      query: vgQuery,
      virtualGraphOptions: runningQueryOptions,
    }),
    [
      runningQueryDataSourceId,
      runningQueryFields,
      forceRefresh,
      isCurrentConnectionIncorrect,
      vgQuery,
      runningQueryOptions,
    ]
  );

  const runningQuery = useProjectDataSourceQuery(runningQueryArgs);

  const didIsQueryingChangeVGItem = useDidValueChange(runningQuery.isQuerying);

  // Process items off the queue
  React.useEffect(() => {
    if (currentQueryQueueItem) {
      if (
        currentConnection &&
        didIsQueryingChangeVGItem &&
        !runningQuery.isQuerying
      ) {
        if (existingVirtualGraph) {
          if (runningQuery.errors.length > 0) {
            runningQuery.errors.forEach(({ message }) => {
              dispatch(
                projectsActionCreators.updatedProjectVirtualGraphPreviewFailure(
                  message
                )
              );
            });
          } else if (runningQuery.fields.length && runningQuery.rows.length) {
            handleUpdateVirtualGraph?.(existingVirtualGraph.id, {
              allResultsIncluded: runningQuery.allResultsIncluded,
              errors: runningQuery.errors,
              from: currentQueryQueueItem.from,
              fields: runningQuery.fields,
              previewRows: runningQuery.rows,
            });
          } else if (forceRefresh) {
            dispatch(
              projectsActionCreators.updatedProjectVirtualGraphPreviewFailure(
                copy.components.projectResourceDialog.projectVirtualGraphPanel
                  .preview.errors.emptyRefresh,
                true
              )
            );
          }
        } else {
          handleUpdateVirtualGraph?.(currentQueryQueueItem.virtualGraphId, {
            allResultsIncluded: runningQuery.allResultsIncluded,
            errors: runningQuery.errors,
            fields: runningQuery.fields,
            previewRows: runningQuery.rows,
          });
          if (runningQueryQueue.length) {
            setRunningQueryQueue((prevQueue) => {
              const newQueue = [...prevQueue];
              newQueue.shift();
              return newQueue;
            });
          }
          if (!selectedTab && setSelectedTab) {
            setSelectedTab(currentQueryQueueItem?.virtualGraphId);
          }
        }
        setForceRefresh(false);
      }
    }
  }, [
    currentConnection,
    currentQueryQueueItem,
    didIsQueryingChangeVGItem,
    dispatch,
    existingVirtualGraph,
    forceRefresh,
    handleAddVirtualGraph,
    handleUpdateVirtualGraph,
    runningQueryQueue,
    runningQuery,
    selectedTab,
    setSelectedTab,
  ]);

  const didVirtualGraphOptionsChange = useDidValueChange(virtualGraphOptions);
  const didIsUsingCustomQueryChange = useDidValueChange(isUsingCustomQuery);

  // Re-add tables/queries to the queue when user toggles between modes
  React.useEffect(() => {
    if (
      didVirtualGraphOptionsChange ||
      (didIsUsingCustomQueryChange && !isUsingCustomQuery)
    ) {
      addItemToQueue(
        ...Object.values(virtualGraphsToCreate)
          .filter((virtualGraph) =>
            isUsingCustomQuery
              ? typeof virtualGraph.from === 'string'
              : typeof virtualGraph.from === 'object'
          )
          .map<QueueItem>((virtualGraph) => ({
            virtualGraphId: virtualGraph.id,
            from: virtualGraph.from,
          }))
      );
    }
  }, [
    didVirtualGraphOptionsChange,
    didIsUsingCustomQueryChange,
    isUsingCustomQuery,
    virtualGraphsToCreate,
    addItemToQueue,
  ]);

  const { isQuerying } = runningQuery;
  const runningQueryStatus = React.useMemo(() => {
    if (isQuerying) {
      return QueryStatus.QUERYING;
    }
    if (existingVirtualGraph) {
      // Errors are shown as notifications when there are prior previewRows
      // to display
      return QueryStatus.EXISTING_ROWS;
    }
    if (
      !selectedTab ||
      !Object.values(virtualGraphsToCreate).filter((virtualGraph) =>
        isUsingCustomQuery
          ? typeof virtualGraph.from === 'string'
          : typeof virtualGraph.from === 'object'
      ).length
    ) {
      return QueryStatus.NO_QUERY;
    }
    const selectedVirtualGraph = virtualGraphsToCreate[selectedTab || ''];
    if (selectedVirtualGraph?.errors?.length) {
      return QueryStatus.ERROR;
    }
    return QueryStatus.SUCCESS;
  }, [
    isQuerying,
    existingVirtualGraph,
    selectedTab,
    virtualGraphsToCreate,
    isUsingCustomQuery,
  ]);

  const handleSelectTable = React.useCallback(
    (tableItem: DataSourceTableSelectItem) => {
      const selectedTableNames: string[] = [];
      getTablesFromVgsToCreate(virtualGraphsToCreate).forEach(
        (virtualGraphToCreate) => {
          const table = virtualGraphToCreate.from as DataSourceTableSelectItem;
          selectedTableNames.push(table.name);
        }
      );

      // Deselect if item already selected
      if (selectedTableNames.includes(tableItem.name)) {
        const virtualGraphToRemove = getTablesFromVgsToCreate(
          virtualGraphsToCreate
        ).find((virtualGraphToCreate) => {
          const table = virtualGraphToCreate.from as DataSourceTableSelectItem;
          return table.name === tableItem.name;
        });

        handleRemoveVirtualGraph?.(virtualGraphToRemove?.id as string);
      } else {
        addItemToQueue({
          virtualGraphId: generateDateRandomId(),
          from: tableItem,
        });
      }
    },
    [addItemToQueue, handleRemoveVirtualGraph, virtualGraphsToCreate]
  );

  const [areResourcePropertiesValid, setAreResourcePropertiesValid] =
    React.useState<boolean>(true);

  const handleCustomQueryTextChange = React.useCallback((customText) => {
    setCustomQueryText(customText);
  }, []);

  // Restore the appropriate running query when the user toggles between modes
  const handleIsUsingCustomQueryChange = React.useCallback(
    (isUsingCustomQuery: boolean) => {
      setIsUsingCustomQuery(isUsingCustomQuery);

      const virtualGraphsArray = Object.values(virtualGraphsToCreate);
      const virtualGraphs = virtualGraphsArray.filter(
        (virtualGraphToCreate) => typeof virtualGraphToCreate.from === 'object'
      );
      const customQuery = virtualGraphsArray.filter(
        (vg) => typeof vg.from === 'string'
      )[0];

      if (isUsingCustomQuery) {
        if (customQuery) {
          setSelectedTab(customQuery.id);
        } else {
          setSelectedTab('');
        }
      } else if (virtualGraphs.length) {
        setSelectedTab(sortVirtualGraphs(virtualGraphsToCreate)[0].id);
      } else {
        setSelectedTab('');
      }
    },
    [setIsUsingCustomQuery, setSelectedTab, virtualGraphsToCreate]
  );

  const handleRefreshPreview = React.useCallback(async () => {
    runningQuery.abortQuery.current();
    setForceRefresh(true);

    addItemToQueue(
      ...Object.values(virtualGraphsToCreate)
        .filter((virtualGraph) =>
          isUsingCustomQuery
            ? typeof virtualGraph.from === 'string'
            : typeof virtualGraph.from === 'object'
        )
        .map<QueueItem>((virtualGraph) => ({
          virtualGraphId: virtualGraph.id,
          from: virtualGraph.from,
        }))
    );
  }, [
    addItemToQueue,
    runningQuery.abortQuery,
    virtualGraphsToCreate,
    isUsingCustomQuery,
  ]);

  const validateCustomQuery = React.useCallback(
    (text: string) => {
      const currentCustomVirtualGraph = Object.values(
        virtualGraphsToCreate
      ).find((virtualGraph) => typeof virtualGraph.from === 'string');
      addItemToQueue({
        from: text,
        virtualGraphId: currentCustomVirtualGraph?.id || generateDateRandomId(),
      });
    },
    [addItemToQueue, virtualGraphsToCreate]
  );

  const [autoRefreshAfterReconnect, setAutoRefreshAfterReconnect] =
    React.useState<boolean>(false);
  const didIsConnectedChange = useDidValueChange(isConnected);
  React.useEffect(() => {
    if (didIsConnectedChange && isConnected && autoRefreshAfterReconnect) {
      handleRefreshPreview();
      setAutoRefreshAfterReconnect(false);
    }
  }, [
    autoRefreshAfterReconnect,
    currentConnection,
    didIsConnectedChange,
    handleRefreshPreview,
    isConnected,
  ]);

  React.useEffect(() => {
    const errors = Object.values(virtualGraphsToCreate).filter(
      (vg) => vg.errors?.length
    );
    const customQueryErrors = errors.filter(
      (vg) => typeof vg.from === 'string'
    );
    const tableErrors = errors.filter((vg) => typeof vg.from === 'object');

    const emptyNames = Object.values(virtualGraphsToCreate).filter(
      (val) => !val.name
    );
    const emptyCustomQueryNames = emptyNames.filter(
      (vg) => typeof vg.from === 'string'
    );
    const emptyTableNames = emptyNames.filter(
      (vg) => typeof vg.from === 'object'
    );
    const customQueryVirtualGraph = getCustomQueryFromVgsToCreate(
      virtualGraphsToCreate
    );
    const tables = getTablesFromVgsToCreate(virtualGraphsToCreate);

    if (isUsingCustomQuery && customQueryErrors.length) {
      setErrorText(
        copy.components.projectResourceDialog.projectVirtualGraphPanel
          .validation.errors
      );
    } else if (!isUsingCustomQuery && tableErrors.length) {
      setErrorText(
        copy.components.projectResourceDialog.projectVirtualGraphPanel.validation.tableErrors(
          tableErrors.map((vg) => {
            const table = vg.from as DataSourceTableSelectItem;
            return table.name;
          })
        )
      );
    } else if (runningQuery.isQuerying) {
      setErrorText(
        copy.components.projectResourceDialog.projectVirtualGraphPanel
          .validation.runningQuery
      );
    } else if (!areResourcePropertiesValid) {
      setErrorText(
        copy.components.projectResourceDialog.projectVirtualGraphPanel
          .validation.invalidResourceProperties
      );
    } else if (
      (isUsingCustomQuery && !customQueryVirtualGraph?.from) ||
      (!isUsingCustomQuery && !tables.length)
    ) {
      setErrorText(
        copy.components.projectResourceDialog.projectVirtualGraphPanel
          .validation.emptyQuery
      );
    } else if (isUsingCustomQuery && emptyCustomQueryNames.length) {
      setErrorText(
        copy.components.projectResourceDialog.projectVirtualGraphPanel
          .validation.name
      );
    } else if (!isUsingCustomQuery && emptyTableNames.length) {
      setErrorText(
        copy.components.projectResourceDialog.projectVirtualGraphPanel.validation.names(
          emptyTableNames.map((vg) => {
            const table = vg.from as DataSourceTableSelectItem;
            return table.name;
          })
        )
      );
    } else {
      setErrorText('');
    }
  }, [
    areResourcePropertiesValid,
    runningQuery.errors.length,
    runningQuery.rows.length,
    setErrorText,
    isUsingCustomQuery,
    virtualGraphsToCreate,
    runningQuery,
  ]);

  const [isSettingsOpen, setIsSettingsOpen] = React.useState(true);

  return (
    <div className="sd-project-virtual-graph-panel">
      {disableDataSourceSidebar || !dataSource ? null : (
        <DataSourcePanelContainer
          dataSource={dataSource}
          type={dataSource.type}
        />
      )}
      <div className="sd-project-virtual-graph-panel__scroll-wrapper">
        <div
          className={classNames(
            'sd-project-virtual-graph-panel__settings',
            Classes.CALLOUT
          )}
        >
          <div className="sd-project-virtual-graph-panel__settings__header">
            <AnchorButton
              icon={
                isSettingsOpen ? IconNames.CHEVRON_DOWN : IconNames.CHEVRON_UP
              }
              minimal
              onClick={() => setIsSettingsOpen(!isSettingsOpen)}
            />
            <H5>Settings</H5>
          </div>

          <Collapse isOpen={isSettingsOpen}>
            <div className="sd-project-virtual-graph-panel__settings__body">
              <ProjectVirtualGraphPanelProperties
                dataSourceType={dataSource?.type || null}
                disableEditing={disableEditing}
                setAreResourcePropertiesValid={setAreResourcePropertiesValid}
                setVirtualGraphOptions={setVirtualGraphOptions}
                updateVirtualGraphOptions={updateVirtualGraphOptions}
                virtualGraphOptions={virtualGraphOptions}
              />
              <Divider />
              {isPendingTables ? (
                <Spinner className="sd-project-virtual-graph-panel__resource-scope" />
              ) : (
                <ProjectVirtualGraphPanelScope
                  customQueryText={customQueryText}
                  disableEditing={disableEditing}
                  handleCustomQueryTextChange={handleCustomQueryTextChange}
                  handleIsUsingCustomQueryChange={
                    handleIsUsingCustomQueryChange
                  }
                  handleRemoveVirtualGraph={handleRemoveVirtualGraph}
                  handleSelectTable={handleSelectTable}
                  isUsingCustomQuery={isUsingCustomQuery}
                  runningQuery={runningQuery}
                  tableMetadataErrorResponse={
                    dataSource?.tableMetadataErrorResponse || null
                  }
                  tables={tables}
                  validateCustomQuery={validateCustomQuery}
                  virtualGraphsToCreate={virtualGraphsToCreate}
                />
              )}
            </div>
          </Collapse>
        </div>

        <ProjectVirtualGraphPreview
          disableEditing={disableEditing}
          enablePreviewRefresh={enablePreviewRefresh}
          existingFields={existingVirtualGraph?.fields}
          existingPreviewTimestamp={existingVirtualGraph?.dateLastUpdated}
          existingRows={existingVirtualGraph?.previewRows}
          handleUpdateVirtualGraph={handleUpdateVirtualGraph}
          isCurrentConnectionIncorrect={isCurrentConnectionIncorrect}
          isUsingCustomQuery={isUsingCustomQuery}
          refreshPreview={handleRefreshPreview}
          runningQueryStatus={runningQueryStatus}
          selectedTab={selectedTab}
          setAutoRefreshAfterReconnect={setAutoRefreshAfterReconnect}
          setSelectedTab={setSelectedTab}
          virtualGraphsToCreate={virtualGraphsToCreate}
        />
      </div>
    </div>
  );
};
